import { useEffect, useMemo, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { formatPercentFromWhole } from "@/utils/number";
import { formatStoreDateRangeShort, getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useConversionRate = (
  storeId: number | null,
  dateRange?: DateRange,
  latestSalesDate?: Date | null,
  deviceType?: DeviceType,
  salesWindowReady = true,
  areaIds?: number[],
): AnalyticsCardData => {
  const areaIdsKey = areaIds?.join(",") ?? "";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{
    rate: number;
    startDate: string;
    endDate: string;
    isLimited: boolean;
  } | null>(null);
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!storeId || !dateRange || !salesWindowReady) {
      setRawData(null);
      return;
    }

    const fetchConversion = async () => {
      try {
        const analytics = AnalyticsServiceV2.getInstance();
        const salesWindow = storePerformanceService.getSalesQueryWindow(
          dateRange,
          latestSalesDate,
          browserTimezone,
        );

        if (!salesWindow.isAvailable) {
          if (isCurrent()) setRawData(null);
          return;
        }
        const params = {
          storeIds: [storeId],
          startDate: salesWindow.startDate,
          endDate: salesWindow.endDate,
          ...(deviceType && deviceType !== "all" ? { deviceType } : {}),
          ...(areaIdsKey ? { areaIds: areaIdsKey.split(",").map(Number) } : {}),
        };

        const sessionRes = await analytics.getSessionAnalytics(params);
        const totalShoppers =
          deviceType === "IN_AISLE"
            ? (sessionRes?.data?.totalSessionsIncludingNoItems ?? 0)
            : (sessionRes?.data?.totalSessions ?? 0);

        if (totalShoppers === 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const salesRes = await analytics.getSalesSummary(params);
        const converted = salesRes?.data?.numberOfConvertedShoppers ?? 0;

        if (converted === 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const rate = (converted / totalShoppers) * 100;
        if (isCurrent()) {
          setRawData({
            rate,
            startDate: salesWindow.startDate,
            endDate: salesWindow.endDate,
            isLimited: salesWindow.isLimited,
          });
        }
      } catch (e) {
        console.error("Error fetching conversion rate:", e);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchConversion();
  }, [
    storeId,
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
    latestSalesDate?.getTime(),
    salesWindowReady,
    deviceType,
    areaIdsKey,
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
      };
    }

    const salesWindow = formatStoreDateRangeShort(
      rawData.startDate,
      rawData.endDate,
      browserTimezone,
      currentLanguage,
      { weekday: "short", month: "short", day: "numeric" },
    );

    return {
      previousValue: null,
      value: formatPercentFromWhole(rawData.rate, currentLanguage),
      subtitle: i18n.t("common.analytics.salesWindow", salesWindow),
      isPositive: true,
      pillText: "",
      rawValue: rawData.rate,
      salesWindow: rawData,
    };
  }, [browserTimezone, rawData, currentLanguage]);
};
